import { useEffect, useMemo, useState } from 'react'
import {
  Brain,
  Calculator,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderInput,
  FolderPlus,
  LogOut,
  MessageSquarePlus,
  Moon,
  PieChart,
  Workflow,
  Pin,
  PinOff,
  Plus,
  Search,
  Settings as SettingsIcon,
  Sun,
  Trash2,
  Users,
} from 'lucide-react'
import type { Chat, MessageHit, Project } from '@shared/types'
import { api } from '../../lib/api'
import { useStore } from '../../store'
import { cn, relativeTime } from '../../lib/utils'

function MoveMenu({ chat, onClose }: { chat: Chat; onClose: () => void }) {
  const projects = useStore((s) => s.projects)
  const assignChat = useStore((s) => s.assignChat)
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); onClose() }} />
      <div className="absolute right-1 top-8 z-50 w-44 rounded-lg border border-[var(--line)] bg-[var(--color-surface-3)] p-1 shadow-[var(--e-3)]">
        <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-fg-faint">Move to project</div>
        {chat.projectId && (
          <button
            onClick={(e) => { e.stopPropagation(); void assignChat(chat.id, null); onClose() }}
            className="block w-full rounded px-2 py-1.5 text-left text-[12px] text-fg-muted hover:bg-[var(--color-surface-2)]"
          >
            Remove from project
          </button>
        )}
        {projects.length === 0 ? (
          <div className="px-2 py-1.5 text-[12px] text-fg-faint">No projects yet</div>
        ) : (
          projects.map((p) => (
            <button
              key={p.id}
              onClick={(e) => { e.stopPropagation(); void assignChat(chat.id, p.id); onClose() }}
              className={cn(
                'block w-full truncate rounded px-2 py-1.5 text-left text-[12px] hover:bg-[var(--color-surface-2)]',
                chat.projectId === p.id ? 'text-fg' : 'text-fg-muted',
              )}
            >
              {p.name}
            </button>
          ))
        )}
      </div>
    </>
  )
}

function ChatRow({ chat, nested }: { chat: Chat; nested?: boolean }) {
  const active = useStore((s) => s.activeChatId === chat.id)
  const selectChat = useStore((s) => s.selectChat)
  const renameChat = useStore((s) => s.renameChat)
  const deleteChat = useStore((s) => s.deleteChat)
  const togglePin = useStore((s) => s.togglePin)
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(chat.title)
  const [menu, setMenu] = useState(false)

  const commit = () => {
    setEditing(false)
    const t = val.trim()
    if (t && t !== chat.title) void renameChat(chat.id, t)
    else setVal(chat.title)
  }

  return (
    <div
      onClick={() => !editing && void selectChat(chat.id)}
      className={cn(
        'group relative flex cursor-pointer items-center gap-2 rounded-lg py-2 pr-1 text-[13px] transition-colors',
        nested ? 'pl-2' : 'pl-2.5',
        active ? 'bg-[var(--accent-wash)] text-fg' : 'text-fg-muted hover:bg-[var(--color-surface-2)] hover:text-fg',
      )}
    >
      {chat.pinned && <Pin size={11} className="shrink-0 text-fg-faint" />}
      {editing ? (
        <input
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit()
            if (e.key === 'Escape') { setVal(chat.title); setEditing(false) }
          }}
          onClick={(e) => e.stopPropagation()}
          className="min-w-0 flex-1 rounded border border-[var(--line)] bg-[var(--color-inset)] px-1.5 py-0.5 text-fg focus:outline-none"
        />
      ) : (
        <span
          onDoubleClick={(e) => { e.stopPropagation(); setEditing(true) }}
          className="min-w-0 flex-1 truncate"
          title={chat.title}
        >
          {chat.title}
        </span>
      )}

      {!editing && (
        <>
          <span className="shrink-0 text-[10px] text-fg-faint group-hover:hidden">
            {relativeTime(chat.updatedAt)}
          </span>
          <div className="hidden shrink-0 items-center gap-0.5 group-hover:flex">
            <button
              onClick={(e) => { e.stopPropagation(); setMenu((m) => !m) }}
              title="Move to project"
              className="rounded p-1 text-fg-subtle hover:text-fg"
            >
              <FolderInput size={13} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); void togglePin(chat) }}
              title={chat.pinned ? 'Unpin' : 'Pin'}
              className="rounded p-1 text-fg-subtle hover:text-fg"
            >
              {chat.pinned ? <PinOff size={13} /> : <Pin size={13} />}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); if (confirm(`Delete “${chat.title}”?`)) void deleteChat(chat.id) }}
              title="Delete"
              className="rounded p-1 text-fg-subtle hover:text-down"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </>
      )}
      {menu && <MoveMenu chat={chat} onClose={() => setMenu(false)} />}
    </div>
  )
}

function ProjectFolder({ project, chats }: { project: Project; chats: Chat[] }) {
  const [open, setOpen] = useState(true)
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(project.name)
  const newChat = useStore((s) => s.newChat)
  const renameProject = useStore((s) => s.renameProject)
  const deleteProject = useStore((s) => s.deleteProject)

  const commit = () => {
    setEditing(false)
    const t = val.trim()
    if (t && t !== project.name) void renameProject(project.id, t)
    else setVal(project.name)
  }

  return (
    <div>
      <div className="group flex items-center gap-1 rounded-lg px-1.5 py-1.5 hover:bg-[var(--color-surface-2)]">
        <button onClick={() => setOpen((o) => !o)} className="text-fg-subtle">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <Folder size={13} className="shrink-0 text-fg-subtle" />
        {editing ? (
          <input
            autoFocus
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit()
              if (e.key === 'Escape') { setVal(project.name); setEditing(false) }
            }}
            className="min-w-0 flex-1 rounded border border-[var(--line)] bg-[var(--color-inset)] px-1.5 py-0.5 text-[12.5px] text-fg focus:outline-none"
          />
        ) : (
          <span
            onDoubleClick={() => setEditing(true)}
            className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-fg-muted"
            title={project.name}
          >
            {project.name}
          </span>
        )}
        <span className="num shrink-0 text-[10px] text-fg-faint group-hover:hidden">{chats.length}</span>
        <div className="hidden shrink-0 items-center gap-0.5 group-hover:flex">
          <button onClick={() => newChat(project.id)} title="New chat in project" className="rounded p-1 text-fg-subtle hover:text-fg">
            <MessageSquarePlus size={13} />
          </button>
          <button
            onClick={() => { if (confirm(`Delete project “${project.name}”? Its chats remain (unfiled).`)) void deleteProject(project.id) }}
            title="Delete project"
            className="rounded p-1 text-fg-subtle hover:text-down"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
      {open && (
        <div className="ml-3 border-l border-[var(--line-soft)] pl-1">
          {chats.length === 0 ? (
            <div className="px-2 py-1.5 text-[11px] text-fg-faint">Empty — drop chats here</div>
          ) : (
            chats.map((c) => <ChatRow key={c.id} chat={c} nested />)
          )}
        </div>
      )}
    </div>
  )
}

function SearchResults({ hits }: { hits: MessageHit[] }) {
  const jumpToMessage = useStore((s) => s.jumpToMessage)
  if (!hits.length)
    return <div className="px-2.5 py-6 text-center text-[12px] text-fg-faint">No matches in conversations</div>
  return (
    <div className="space-y-1">
      {hits.map((h, i) => (
        <button
          key={i}
          onClick={() => void jumpToMessage(h.chatId, h.messageId)}
          className="block w-full rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-[var(--color-surface-2)]"
        >
          <div className="truncate text-[12.5px] font-medium text-fg">{h.chatTitle}</div>
          <div className="line-clamp-2 text-[11px] text-fg-subtle">{h.snippet}</div>
        </button>
      ))}
    </div>
  )
}

export function Sidebar() {
  const chats = useStore((s) => s.chats)
  const projects = useStore((s) => s.projects)
  const newChat = useStore((s) => s.newChat)
  const createProject = useStore((s) => s.createProject)
  const toggleCalculator = useStore((s) => s.toggleCalculator)
  const toggleMemory = useStore((s) => s.toggleMemory)
  const toggleAdmin = useStore((s) => s.toggleAdmin)
  const setView = useStore((s) => s.setView)
  const openWorker = useStore((s) => s.openWorker)
  const logout = useStore((s) => s.logout)
  const user = useStore((s) => s.user)
  const theme = useStore((s) => s.theme)
  const setTheme = useStore((s) => s.setTheme)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<MessageHit[] | null>(null)

  const searching = q.trim().length >= 2

  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) {
      setHits(null)
      return
    }
    let alive = true
    const t = setTimeout(() => {
      api
        .searchAll(term)
        .then((h) => alive && setHits(h))
        .catch(() => alive && setHits([]))
    }, 250)
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [q])

  const unfiled = useMemo(() => chats.filter((c) => !c.projectId), [chats])
  const chatsByProject = useMemo(() => {
    const m = new Map<string, Chat[]>()
    for (const c of chats) {
      if (!c.projectId) continue
      const arr = m.get(c.projectId)
      if (arr) arr.push(c)
      else m.set(c.projectId, [c])
    }
    return m
  }, [chats])

  const footerBtn =
    'inline-flex h-9 w-9 items-center justify-center rounded-lg text-fg-subtle hover:bg-[var(--color-surface-2)] hover:text-fg'

  return (
    <aside className="flex h-full w-[272px] max-w-[82vw] shrink-0 flex-col border-r border-[var(--line-soft)] bg-[var(--color-surface-1)]">
      <div className="flex items-center gap-1.5 px-3 pb-2 pt-3">
        <button
          onClick={() => newChat()}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[var(--line)] bg-[var(--color-surface-2)] py-2 text-[13px] font-medium text-fg transition-colors hover:bg-[var(--color-surface-3)]"
        >
          <Plus size={15} /> New chat
        </button>
        <button
          onClick={() => void createProject()}
          title="New project"
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--line)] bg-[var(--color-surface-2)] text-fg-muted transition-colors hover:bg-[var(--color-surface-3)] hover:text-fg"
        >
          <FolderPlus size={16} />
        </button>
      </div>

      <div className="px-3 pb-2">
        <div className="flex items-center gap-2 rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-2.5 py-1.5">
          <Search size={14} className="text-fg-faint" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search chats & messages"
            className="w-full bg-transparent text-[12.5px] text-fg placeholder:text-fg-faint focus:outline-none"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-3">
        {searching ? (
          hits === null ? (
            <div className="px-2.5 py-6 text-center text-[12px] text-fg-faint">Searching…</div>
          ) : (
            <SearchResults hits={hits} />
          )
        ) : (
          <div className="space-y-2">
            {projects.length > 0 && (
              <div className="space-y-0.5">
                {projects.map((p) => (
                  <ProjectFolder key={p.id} project={p} chats={chatsByProject.get(p.id) ?? []} />
                ))}
              </div>
            )}
            <div>
              {projects.length > 0 && unfiled.length > 0 && (
                <div className="px-2 pb-1 pt-1 text-[10px] uppercase tracking-[0.12em] text-fg-faint">
                  Chats
                </div>
              )}
              <div className="space-y-0.5">
                {unfiled.length === 0 && projects.length === 0 ? (
                  <div className="px-2.5 py-6 text-center text-[12px] text-fg-faint">No chats yet</div>
                ) : (
                  unfiled.map((c) => <ChatRow key={c.id} chat={c} />)
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* mobile-only footer (no rail on mobile) */}
      <div className="flex items-center justify-around border-t border-[var(--line-soft)] px-2 py-2 md:hidden">
        <button onClick={() => setView('portfolio')} title="Portfolio" className={footerBtn}>
          <PieChart size={18} />
        </button>
        <button onClick={() => toggleCalculator(true)} title="Calculator" className={footerBtn}>
          <Calculator size={18} />
        </button>
        <button onClick={() => toggleMemory(true)} title="Memory" className={footerBtn}>
          <Brain size={18} />
        </button>
        {user?.role === 'admin' && (
          <button onClick={() => openWorker(null)} title="Workers" className={footerBtn}>
            <Workflow size={18} />
          </button>
        )}
        {user?.role === 'admin' && (
          <button onClick={() => toggleAdmin(true)} title="Users & usage" className={footerBtn}>
            <Users size={18} />
          </button>
        )}
        <button onClick={() => setView('settings')} title="Settings" className={footerBtn}>
          <SettingsIcon size={18} />
        </button>
        <button
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          title="Toggle theme"
          className={footerBtn}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={17} />}
        </button>
        <button onClick={() => void logout()} title="Sign out" className={footerBtn}>
          <LogOut size={18} />
        </button>
      </div>
    </aside>
  )
}
